import React, { useState, useEffect } from 'react';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { useUnderReviewStore } from 'src/store/under-review';
import { useFinalSubmissionStore } from 'src/store/final-submission';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { usePlan } from '@features/onboarding/hooks/usePlan';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';
import { NoWorriesStep } from '../no-worries/NoWorriesStep';
import { HumanReviewStep } from '../human-review/HumanReviewStep';
import { CongratulationsStep } from '../congratulations/CongratulationsStep';
import { ApiErrorStep } from '../api-error/ApiErrorStep';
import { NoPlansSelected } from '../no-plans-selected/NoPlansSelected';
import { finishStepsContent } from '../../content/content';
import { cn } from '@utils/index';
import { useApplicationStore } from 'src/store/application';
import { useGeneralStore } from 'src/store';
import { useAIStore } from 'src/store/ai';
import { useInsuranceStore } from 'src/store/insurance';
import {
  resolveUserState,
  isPlanAvailableInState,
} from '@utils/planAvailability';

const components = {
  NoWorriesStep,
  HumanReviewStep,
  CongratulationsStep,
  ApiErrorStep,
  NoPlansSelected,
} as any;

type EmptyStateVariant =
  | 'default'
  | 'browsing'
  | 'maxedOut'
  | 'rejectedCoverage';

const NO_PLANS_COPY: Record<
  EmptyStateVariant,
  { title: string; description: string; helper?: string }
> = {
  default: {
    title: "Oops — you didn't pick a plan.",
    description: "It's like getting to checkout with an empty cart.",
    helper: 'Click the Previous button and choose a plan or two, or three.',
  },
  browsing: {
    title: 'Just browsing? Totally fine.',
    description:
      "Our doors are always open. Come back and explore when you're ready.",
  },
  maxedOut: {
    title: 'You’re maxed out on awesome!',
    description:
      'Looks like you already have every available plan. No need to add more - you’ve reached peak coverage!',
  },
  rejectedCoverage: {
    title: 'You’ve made some wise choices.',
    description:
      'The plans you selected still offer solid benefits and peace of mind.',
  },
};

const SUPPLEMENTAL_ONLY_STATES = new Set(['ND', 'SD', 'ID', 'MD']);
const MEDICAL_AND_VISION_ONLY_STATES = new Set(['NY', 'WA', 'NM', 'MT']);

const GENERAL_ACCEPTED_COPY = {
  title: 'Congratulations! You’re eligible to enroll.',
  description:
    'We’ve selected the coverage options available to you based on your eligibility and state.<br /><br />Review your available plans and choose the coverage that’s right for you.',
};

const DEFAULT_ACCEPTED_COPY = {
  ...GENERAL_ACCEPTED_COPY,
};

const SUPPLEMENTAL_ONLY_ACCEPTED_COPY = {
  ...GENERAL_ACCEPTED_COPY,
};

const MEDICAL_AND_VISION_ONLY_ACCEPTED_COPY = {
  ...GENERAL_ACCEPTED_COPY,
};

const VISION_ONLY_ACCEPTED_COPY = {
  ...GENERAL_ACCEPTED_COPY,
};

const DynamicComponent = ({
  componentName,
  handleNextCompatible,
  componentProps = {},
}: {
  componentName: string;
  handleNextCompatible: () => void;
  componentProps?: Record<string, unknown>;
}) => {
  const Component = components[componentName] || 'NoWorriesStep';

  if (componentName === 'CongratulationsStep') {
    return (
      <Component
        question={{ question: "Let’s see the plans" }}
        onSubmit={handleNextCompatible}
      />
    );
  }

  return <Component {...componentProps} />;
};

export const EstimateResult: React.FC<FormStepProps> = () => {
  const { isUnderReview, sessionLockedForHumanReview } = useUnderReviewStore();
  const { underwritingResult, isUnderwritingLoading, isSubmittingApplication } =
    useFinalSubmissionStore();
  const { plans: chosenPlans } = usePlan();
  const { isLocalLoading, handlePreviousStep, handleJumpTo, formSteps } =
    useFormControls();
  const [showCongratulations, setShowCongratulations] = useState(false);
  const { phqUnderReviewAt, phqAcceptedAt, phqRejectedAt, applications } = useApplicationStore();
  const { data } = useOnboardingStore();
  const { siteConfig } = useGeneralStore();
  const fullPlans = useInsuranceStore((state) => state.plans);
  const hasAnyAvailablePlan = siteConfig?.plans?.length && siteConfig.plans.length > 0;
  const noPlansCopy = NO_PLANS_COPY.default;
  const appAddress = applications?.[0]?.address as
    | { zipcode?: string; state?: string }
    | undefined;
  const userState = (
    resolveUserState(appAddress?.zipcode, appAddress?.state) ||
    resolveUserState(data?.address?.zipcode, data?.address?.state) ||
    ''
  ).toUpperCase();
  const shouldShowSupplementalOnlyMessage =
    SUPPLEMENTAL_ONLY_STATES.has(userState);
  const shouldShowMedicalAndVisionOnlyMessage =
    MEDICAL_AND_VISION_ONLY_STATES.has(userState);
  const shouldShowVisionOnlyMessage = userState === 'MN';
  const configuredPlans = React.useMemo(() => {
    if (!siteConfig?.plans?.length) return [];

    const allowedPlanIds = new Set(siteConfig.plans);
    return fullPlans.filter((plan) => allowedPlanIds.has(plan.id));
  }, [fullPlans, siteConfig?.plans]);
  const configuredMedicalPlans = React.useMemo(
    () => configuredPlans.filter((plan) => plan.category === 'Major Medical'),
    [configuredPlans]
  );
  const availablePlansInState = React.useMemo(
    () =>
      configuredPlans.filter((plan) =>
        isPlanAvailableInState(plan, userState)
      ),
    [configuredPlans, userState]
  );
  const hasAvailableMedicalPlans = availablePlansInState.some(
    (plan) => plan.category === 'Major Medical'
  );
  const hasAvailableAncillaryPlans = availablePlansInState.some(
    (plan) => plan.category && plan.category !== 'Major Medical'
  );
  const shouldShowMedicalStateUnavailableMessage = Boolean(
    userState &&
      configuredMedicalPlans.length > 0 &&
      !hasAvailableMedicalPlans &&
      hasAvailableAncillaryPlans
  );
  const shouldShowMedicalStateUnavailableResult =
    shouldShowMedicalStateUnavailableMessage &&
    (showCongratulations || phqAcceptedAt);
  const medicalStateUnavailableVariant =
    userState === 'MN'
      ? 'mnMedicalStateUnavailable'
      : 'medicalStateUnavailable';
  const medicalStateUnavailableTitle =
    userState === 'MN'
      ? "We're sorry, but we can't approve you for many of our plans at this time, but we can still give you some protection..."
      : "We're sorry, but we can't approve you for one of our health plans this time, but we can still protect you in other ways...";
  const acceptedCopy = shouldShowVisionOnlyMessage
    ? VISION_ONLY_ACCEPTED_COPY
    : shouldShowMedicalAndVisionOnlyMessage
    ? MEDICAL_AND_VISION_ONLY_ACCEPTED_COPY
    : shouldShowSupplementalOnlyMessage
      ? SUPPLEMENTAL_ONLY_ACCEPTED_COPY
      : DEFAULT_ACCEPTED_COPY;

  const hasAnyPlan = !!chosenPlans.length;
  console.log('chosenPlans', chosenPlans)
  const finishStep = hasAnyPlan
    ? finishStepsContent.find((s) => s.id === 'congratulations')
    : finishStepsContent.find((s) => s.id === 'coverageReassurance');

  useEffect(() => {
    setShowCongratulations(underwritingResult?.status === 'accepted');
  }, [underwritingResult?.status]);

  // Override the AI chat page context so the chatbot knows the actual
  // underwriting outcome instead of always seeing "approved".
  useEffect(() => {
    if (sessionLockedForHumanReview || underwritingResult?.status === 'human_review' || isUnderReview || phqUnderReviewAt) {
      useAIStore.getState().setCurrentPage('human_review');
    } else if (
      shouldShowMedicalStateUnavailableResult ||
      underwritingResult?.status === 'rejected' ||
      phqRejectedAt
    ) {
      useAIStore.getState().setCurrentPage('rejected');
    } else if (showCongratulations || phqAcceptedAt) {
      useAIStore.getState().setCurrentPage('approved');
    }
  }, [underwritingResult?.status, phqRejectedAt, phqAcceptedAt, phqUnderReviewAt, isUnderReview, sessionLockedForHumanReview, showCongratulations, shouldShowMedicalStateUnavailableResult]);

  type EstimateResultUI = {
    title?: string;
    description?: string;
    nextButtonLabel?: string;
    hideNextButton?: boolean;
    key?: string;
    componentName: 'NoWorriesStep' | 'HumanReviewStep' | 'CongratulationsStep' | 'ApiErrorStep' | 'NoPlansSelected';
    hidePrevButton?: boolean;
    componentProps?: Record<string, unknown>;
  };

  const ui: EstimateResultUI = React.useMemo(() => {
    if (sessionLockedForHumanReview || underwritingResult?.status === 'human_review' || isUnderReview || phqUnderReviewAt) {
      return {
        title: `Application Review Required.`,
        description: '',
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'HumanReviewStep',
        key: 'humanReview',
        hidePrevButton: true,
      };
    }

    if (shouldShowMedicalStateUnavailableResult) {
      return {
        title: medicalStateUnavailableTitle,
        nextButtonLabel: 'Next',
        hideNextButton: false,
        componentName: 'NoWorriesStep',
        key: `medicalStateUnavailable-${userState}`,
        hidePrevButton: true,
        componentProps: { variant: medicalStateUnavailableVariant },
      };
    }

    if (showCongratulations || phqAcceptedAt) {
      return {
        title: acceptedCopy.title,
        description: acceptedCopy.description,
        nextButtonLabel: 'Next',
        hideNextButton: false,
        componentName: 'CongratulationsStep',
        key: 'congratulations',
        hidePrevButton: true,
      };
    }

    // Handle API errors - show technical error message
    if (underwritingResult?.status === 'rejected' && underwritingResult?.details?.reason === 'API Error') {
      return {
        title: 'Technical Error.',
        description: 'We\'re experiencing technical difficulties. Please try again.',
        nextButtonLabel: 'Try Again',
        hideNextButton: false,
        componentName: 'ApiErrorStep',
        key: 'apiError',
        hidePrevButton: true,
      };
    }

    if (underwritingResult?.status === 'rejected' || phqRejectedAt) {
      return {
        title: 'We\'re sorry, but we can\'t approve you for one of our health plans this time, but we can still protect you in other ways...',
        // description: 'Thank you for completing your health questionnaire. Let\'s continue with other coverage options.',
        nextButtonLabel: 'Next',
        hideNextButton: false,
        componentName: 'NoWorriesStep',
        key: 'rejected',
        hidePrevButton: true,
      };
    }

    if (!hasAnyAvailablePlan) {
      return {
        title: noPlansCopy.title,
        description: noPlansCopy.description,
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'NoPlansSelected',
        key: 'noPlansSelected',
        componentProps: { copy: noPlansCopy },
      };
    }

    if (!hasAnyPlan) {
      return {
        title: finishStep?.title,
        description: finishStep?.description,
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'NoWorriesStep',
        key: 'noWorries',
        hidePrevButton: false,
      };
    }

    return {
      title: '',
      description: '',
      nextButtonLabel: undefined,
      hideNextButton: true,
      componentName: 'NoWorriesStep',
      key: 'noWorries',
      hidePrevButton: false,
    };
  }, [showCongratulations, hasAnyPlan, underwritingResult, isUnderReview, sessionLockedForHumanReview, phqUnderReviewAt, phqAcceptedAt, phqRejectedAt, acceptedCopy, shouldShowMedicalStateUnavailableResult, medicalStateUnavailableTitle, medicalStateUnavailableVariant, userState]);

  const { title, description, nextButtonLabel, hideNextButton, componentName, key, hidePrevButton, componentProps } = ui;

  const handleNextCompatible = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();

    if (underwritingResult?.status === 'human_review') {
      return;
    }

    // Handle API error - go back to health questions to retry
    if (underwritingResult?.status === 'rejected' && underwritingResult?.details?.reason === 'API Error') {
      console.log('[EstimateResult] API error, navigating back to health questions to retry');
      handleJumpTo(FormStepSections.HealthQuestion);
      return;
    }

    if (nextButtonLabel === 'Next') {
      if (showCongratulations) {
        setShowCongratulations(false);
      }

      // Steps can be hidden by state (forbiddenStates), so navigate to the
      // first step that actually exists instead of a hardcoded section.
      const hasSection = (section: string) =>
        formSteps.some((s) => s.sectionName === section);
      const ancillaryOrFinish = hasSection(FormStepSections.Dental)
        ? FormStepSections.Dental
        : FormStepSections.Finish;

      if (shouldShowMedicalStateUnavailableResult) {
        console.log('[EstimateResult] Medical unavailable in state, removing medical plan and navigating to ancillary');
        useOnboardingStore.getState().removeMedicalPlan();
        handleJumpTo(ancillaryOrFinish);
        useOnboardingStore.getState().setEstimateResultSeen(true);
        return;
      }

      // DTQ: medical plans are hidden, so skip straight to ancillary (Dental).
      if (underwritingResult?.status === 'rejected' || phqRejectedAt) {
        console.log('[EstimateResult] User rejected, removing medical plan and navigating to ancillary');
        useOnboardingStore.getState().removeMedicalPlan();
        useOnboardingStore.getState().rejectMedicalPlan();
        handleJumpTo(ancillaryOrFinish);
        useOnboardingStore.getState().setEstimateResultSeen(true);
        return;
      }

      // Accepted/other: go to Medical if it exists, else ancillary, else Finish.
      const target = hasSection(FormStepSections.Medical)
        ? FormStepSections.Medical
        : ancillaryOrFinish;
      console.log('[EstimateResult] Navigating to first available plan step:', target);
      handleJumpTo(target);
      useOnboardingStore.getState().setEstimateResultSeen(true);
      return;
    }
  };

  const handlePrev = (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();

    if (underwritingResult?.status === 'human_review') {
      return;
    }

    if (showCongratulations) {
      return;
    }

    handlePreviousStep();
  };

  return (
    <OnboardingFormStepLayout
      handleFormSubmit={handleNextCompatible}
      onPrev={hidePrevButton ? undefined : handlePrev}
      title={title}
      description={description}
      hideNextButton={hideNextButton}
      isSubmitText={false}
      onlyImage={!hasAnyPlan}
      isLoading={isUnderwritingLoading || isSubmittingApplication || isLocalLoading}
      nextButtonLabel={nextButtonLabel}
      headerAnimationKey={key}
    >
      <div className={cn('h-full w-full rounded-2xl')}>
        <div className="pb-4 h-full w-full justify-center">
          <DynamicComponent
            handleNextCompatible={handleNextCompatible}
            componentName={componentName}
            componentProps={componentProps}
          />
        </div>
      </div>
    </OnboardingFormStepLayout>
  );
};
